import { CallScreen } from '../callScreen/callScreen.js';
import { createTranslator } from '../callScreen/callScreenStrings.js';
import { traceCall } from './callTrace.js';

// The component's own call states -> the call screen's phases.
const PHASE_OF = {
    outgoing_ringing: 'dialing',
    incoming_ringing: 'incoming',
    connecting: 'connecting',
    in_call: 'active',
};

// A hang-up that has not ended the call by then is forced: the screen must
// never stay up on a call the user already ended.
const HANGUP_WATCHDOG_MS = 6000;

export const dialogMixin = {

    //********************* SHOW CALL DIALOG ********************************//
    // Opens the call screen for either direction; further changes while the
    // call is up go through updateDialogState/attachMediaStreams instead of
    // opening it again.
    // * otherUser (Object|null) — { fullname, avatar_128, avatar_512 }
    // * callState (String) — what the screen shows first, the call's own state
    //   by default. 'connecting' is display-only: a call answered on the native
    //   screen, while this side sets it up (see nativeCallsMixin).
    // * direction ('outgoing'|'incoming') — defaults from callState.
    showCallDialog({ otherUser, callState = this.callState, direction } = {}) {
        // An ended call may still be on screen for a moment; a new call
        // replaces it at once.
        this._callScreen?.close({ immediate: true });
        clearTimeout(this._hangupWatchdog);

        this._t ??= createTranslator();
        const resolvedDirection = direction || (callState === 'outgoing_ringing' ? 'outgoing' : 'incoming');
        const inApp = Boolean(this.isNativeShell?.());

        const screen = new CallScreen({
            t: this._t,
            handlers: {
                accept: () => this.acceptIncomingCall(),
                decline: () => this.rejectIncomingCall(),
                hangup: () => this._hangupFromScreen(),
                mute: () => this.toggleMute(),
                toggleSpeaker: () => this.toggleSpeaker?.(),
                selectRoute: (route, id) => this.selectAudioRoute?.(route, id),
                camera: () => this.toggleCamera(),
                close: () => this._dismissCallScreen(screen),
                retry: () => this._retryCall(screen),
                openSettings: () => this.openAppSettings?.(),
            },
        });
        // Whose call this screen shows: a SIP.js session in this page, or the
        // native engine's. Each side ends only its own screen - a page call
        // hung up because a native call took over must not close the native
        // call's screen as it goes.
        screen.owner = this.nativeCallView ? 'native' : 'sip';
        this._callScreen = screen;
        // Kept for code that only checks whether a call window is up.
        this.dialogScope = screen;

        screen.open({
            phase: PHASE_OF[callState] || 'connecting',
            direction: resolvedDirection,
            name: otherUser?.fullname || '',
            avatarUrl: otherUser?.avatar_128 || otherUser?.avatar_512 || '',
            muted: Boolean(this.isMuted),
            video: this._callScreenVideo(),
            audio: this._audioRouteState || null,
            connectedAt: callState === 'in_call' ? Date.now() : null,
            inApp,
            canOpenSettings: inApp && Boolean(this._voipSupports?.('openSettings')),
            canRetry: resolvedDirection === 'outgoing' && Boolean(this._lastCallee),
            micBlocked: false,
        });
        traceCall('callScreen', { phase: screen.state.phase, direction: resolvedDirection });

        this.onCallScreenOpened?.();
    },

    //********************* CLOSE CALL DIALOG ********************************//
    // At once, without the "Call ended" moment - for a screen being replaced,
    // or a call the user declined themselves.
    // * owner ('sip'|'native'|undefined) — only close a screen showing that kind of call
    closeCallDialog(owner) {
        const screen = this._callScreen;
        if(!screen) return;
        if(owner && screen.owner !== owner) return;
        this._forgetCallScreen(screen);
        screen.close({ immediate: true });
    },

    //********************* END CALL DIALOG ********************************//
    // Shows how the call ended, then closes by itself (or on Close / Call
    // again). The call itself is already over: the component is ready for the
    // next one while this is on screen.
    // * end ({ reason }) — see callScreenModel.js's END_TEXT
    // * owner ('sip'|'native'|undefined) — only end a screen showing that kind of call
    endCallDialog(end = { reason: 'ended' }, owner) {
        const screen = this._callScreen;
        if(!screen || !screen.isOpen) return;
        if(owner && screen.owner !== owner) return;
        clearTimeout(this._hangupWatchdog);

        if(screen.state.phase === 'ended') {
            // SIP.js reports Terminated before the error that caused it: a
            // more specific reason arriving right after a plain "ended" wins.
            if(end.reason !== 'ended' && screen.state.end?.reason === 'ended' && Date.now() - screen.state.endedAt < 1000) {
                screen.update({ end });
            }
            return;
        }

        traceCall('callScreenEnded', { reason: end.reason });
        screen.update({ phase: 'ended', end, endedAt: Date.now() });
        // The call no longer needs the screen awake, the proximity sensor or
        // the audio routes.
        this.onCallScreenEnded?.();
    },

    //********************* UPDATE DIALOG STATE ********************************//
    // Accepts the component's own vocabulary (callState, isMuted, callVideo,
    // isCameraOff) as well as the screen's (phase, muted, audio, ...).
    // * patch (Object)
    updateDialogState(patch = {}) {
        const screen = this._callScreen;
        if(!screen || !screen.isOpen || screen.state.phase === 'ended') return;

        const next = {};
        for(const key of ['phase', 'muted', 'audio', 'micBlocked', 'name', 'avatarUrl', 'connectedAt', 'end']) {
            if(key in patch) next[key] = patch[key];
        }
        if('callState' in patch) next.phase = PHASE_OF[patch.callState] || screen.state.phase;
        if('isMuted' in patch) next.muted = Boolean(patch.isMuted);
        if('callVideo' in patch || 'isCameraOff' in patch) next.video = this._callScreenVideo();

        // The timer runs from the moment both sides were connected - unless
        // the caller knows better (a native call connected while the WebView
        // slept carries its own connectedAt).
        if(next.phase === 'active' && !screen.state.connectedAt && !next.connectedAt) next.connectedAt = Date.now();

        screen.update(next);
    },

    _callScreenVideo() {
        // Video is not built for the mobile apps yet: the camera button is a
        // placeholder there. In a browser it is the real toggle it was.
        const available = !this.isNativeShell?.() && !this.nativeCallView;
        return {
            available,
            local: available && Boolean(this.callVideo) && !this.isCameraOff,
            remote: available && Boolean(this.callVideo),
        };
    },

    //********************* SCREEN ACTIONS ********************************//
    _hangupFromScreen() {
        if(this.callState === 'outgoing_ringing' && !this.nativeCallView) this.cancelOutgoingCall();
        else this.hangupCall();

        // Whatever the network does, the user's hang-up wins.
        clearTimeout(this._hangupWatchdog);
        const screen = this._callScreen;
        this._hangupWatchdog = setTimeout(() => {
            if(this._callScreen !== screen || !screen?.isOpen || screen.state.phase === 'ended') return;
            traceCall('hangupWatchdog', { callState: this.callState, native: Boolean(this.nativeCallView) });
            if(this.nativeCallView) {
                this.nativeCallView = null;
                this.endCallDialog({ reason: 'ended' });
            } else {
                this.teardownCall({ reason: 'ended' });
            }
        }, HANGUP_WATCHDOG_MS);
    },

    _dismissCallScreen(screen) {
        // Close on a call that is still going on (only reachable by a stuck
        // state) hangs it up rather than hiding it.
        if(screen.state.phase !== 'ended') return this._hangupFromScreen();
        this._forgetCallScreen(screen);
        screen.close();
    },

    _retryCall(screen) {
        const callee = this._lastCallee;
        this._forgetCallScreen(screen);
        screen.close({ immediate: true });
        if(callee) this.startOutgoingCall(callee);
    },

    _forgetCallScreen(screen) {
        if(this._callScreen !== screen) return;
        this._callScreen = null;
        this.dialogScope = null;
        clearTimeout(this._hangupWatchdog);
        this.onCallScreenClosed?.();
    },

    //********************* ATTACH MEDIA STREAMS ********************************//
    // Pulls local/remote tracks off the active session's peerConnection into the
    // call screen's <video> elements. Single-active-call assumption (enforced
    // by the call state machine): only one call screen exists at a time.
    attachMediaStreams() {
        const sessionDescriptionHandler = this.session?.sessionDescriptionHandler;
        const peerConnection = sessionDescriptionHandler?.peerConnection;
        if(!peerConnection) return;

        // SIP.js's SDH maintains remoteMediaStream itself and keeps adding
        // tracks as they arrive - safer than a one-shot getReceivers()
        // snapshot, which permanently misses any track that shows up after
        // Established (seen live as "RTP delivered but nothing plays" on the
        // answering side). Fall back to the snapshot for safety.
        let remoteStream = sessionDescriptionHandler.remoteMediaStream;
        if(!remoteStream) {
            remoteStream = new MediaStream();
            peerConnection.getReceivers().forEach((receiver) => {
                if(receiver.track) remoteStream.addTrack(receiver.track);
            });
        }

        const localStream = new MediaStream();
        peerConnection.getSenders().forEach((sender) => {
            if(sender.track) localStream.addTrack(sender.track);
        });
        // teardownCall stops sessionDescriptionHandler.localMediaStream's
        // tracks (not the peer-connection-sender-derived localStream above):
        // if the far end's SDP answer rejects an m-line (e.g. FreeSWITCH
        // rejecting video on an audio-only profile), that track never becomes
        // a sender, so it'd never show up in getSenders() - but it was still
        // captured from the camera/mic via getUserMedia and stays live,
        // holding the hardware open, unless stopped via this full reference.
        this._localStream = sessionDescriptionHandler.localMediaStream;

        const remoteVideo = this._callScreen?.remoteVideo;
        const localVideo = this._callScreen?.localVideo;
        // Explicit play() instead of trusting the autoplay attribute: if the
        // browser blocks/skips autoplay at srcObject-assignment time, the
        // element stays paused forever and never retries on its own - RTP
        // keeps arriving but nothing is heard. Confirmed live as the "packets
        // delivered, recipient hears nothing" failure mode.
        if(remoteVideo) {
            // Assigned again even when it is the same stream: a video track
            // added to an already-bound stream can silently never render until
            // srcObject is set again (see watchForVideoTrack).
            remoteVideo.srcObject = remoteStream;
            remoteVideo.play().catch((err) => console.warn('Remote media playback failed', err));
            // If tracks arrive after Established (late video, renegotiation),
            // kick playback again - a paused element does not resume itself.
            remoteStream.onaddtrack = () => {
                remoteVideo.play().catch((err) => console.warn('Remote media playback failed', err));
            };
        }
        if(localVideo) {
            // A muted element: the local microphone is never played back.
            localVideo.srcObject = localStream;
            localVideo.play().catch(() => {});
        }
    },
};
